import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import moment from 'moment';
import { ConsolGroupService } from './consol-group.service';
import { safeJsonInput } from '../../../shared/_utils/safe-json-input';

@Component({
    selector: 'gtw-consol-group',
    templateUrl: './consol-group.component.html',
    styleUrls: ['./consol-group.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ConsolGroupComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;

  // Modal properties
  modalTitle = "Maintain Consolidated Group";
  isEdit = false;
  showFormLoader = false;

  // Entity data properties
  entityList: any[] = [];
  consolEntity: any = {};
  consolEntity_backup: any = {};

  // Date picker properties
  dateOptions: any = {};

  // Global data properties
  globalService: any;
  GENERAL_CONFIG: any;
  USER_SETTINGS: any;
  SERVER_CONFIG: any;
  filterParams: any = {};
  rowData: any = {};

  constructor(
    private consolGroupService: ConsolGroupService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  @Input('base-urls')
  set _base_urls(baseUrls: string) {
    if (!baseUrls) return;
    const parsed = safeJsonInput<any>(baseUrls, null, 'consol-group.base-urls');
    if (parsed) {
      this.GENERAL_CONFIG = parsed;
    }
  }

  @Input('header-data')
  set _header_data(headerData: string) {
    if (!headerData) return;
    const parsedData = safeJsonInput<any>(headerData, null, 'consol-group.header-data');
    if (parsedData) {
      this.globalService = parsedData.globalParams;
      this.USER_SETTINGS = parsedData.userSettingsData;
      this.SERVER_CONFIG = parsedData.serverConfigData;
      this.GENERAL_CONFIG = parsedData.generalConfigData;
    }
  }

  @Input('row-data')
  set _row_data(rowData: string) {
    if (!rowData) return;
    const parsed = safeJsonInput<any>(rowData, null, 'consol-group.row-data');
    if (parsed) {
      this.rowData = parsed;
      this.init();
      this.cdr.detectChanges();
    }
  }

  @Input('filter-params')
  set _filter_params(filterParams: string){
    const params = safeJsonInput<any>(filterParams, null, 'consol-group.filter-params');
    if (params && Object.keys(params).length > 0) {
      this.filterParams = params;
      this.init();
      this.cdr.detectChanges();
    }
  }

  ngOnInit(): void {
    this.init();
  }

  init() {
    try {
      // Initialize if we have either filterParams or rowData
      if ((this.filterParams && Object.keys(this.filterParams).length > 0) || 
          (this.rowData && Object.keys(this.rowData).length > 0)) {
        this.setupEntityData();
        this.initializeConsolEntity();
      }
    } catch (e) {
      console.error('Error in ConsolGroup init:', e);
    }
  }

  private setupEntityData() {
    // Initialize with filter params or rowData
    const taxYear = this.filterParams?.tax_year || this.rowData?.TAX_YEAR || '';
    
    this.entityList = [
      {key: "Tax Year", value: taxYear},
      {key: "User", value: this.globalService?.user_name || ''},
      {key: "System", value: this.globalService?.system_name || ''}
    ];
  }

  private initializeConsolEntity() {
    // Initialize with default values, using filterParams or rowData for tax year
    const taxYear = this.filterParams?.tax_year || this.rowData?.TAX_YEAR || '';
    
    this.consolEntity = {
      CONSOL_GROUP_KEY: '',
      TAX_YEAR: taxYear,
      GROUP_NAME: '',
      RETURN_DATE: '',
      IRS_CENTER_NAME: '',
      PARENT_LEID: '',
      FILER_TAX_YEAR_BEGIN: '',
      FILER_TAX_YEAR_END: '',
      SHORT_DISPLAY_NAME: '',
      GILTI_CONSOL_GROUP_KEY: '',
    };

    // If editing existing record
    if (this.rowData && Object.keys(this.rowData).length > 0) {
      this.isEdit = true;
      
      // Merge rowData with default structure to ensure all fields exist
      this.consolEntity = {
        ...this.consolEntity,
        ...this.rowData
      };
      
      // Convert date strings to proper format for HTML date inputs
      if (this.consolEntity.RETURN_DATE) {
        this.consolEntity.RETURN_DATE = this.formatDateForInput(this.consolEntity.RETURN_DATE);
      }
      if (this.consolEntity.FILER_TAX_YEAR_BEGIN) {
        this.consolEntity.FILER_TAX_YEAR_BEGIN = this.formatDateForInput(this.consolEntity.FILER_TAX_YEAR_BEGIN);
      }
      if (this.consolEntity.FILER_TAX_YEAR_END) {
        this.consolEntity.FILER_TAX_YEAR_END = this.formatDateForInput(this.consolEntity.FILER_TAX_YEAR_END);
      }
    }

    this.consolEntity_backup = { ...this.consolEntity };
  }

  isValid(): boolean {
    // Reset validation flags
    this.consolEntity.CONSOL_GROUP_NOT_VALID = false;
    this.consolEntity.TAX_YEAR_NOT_VALID = false;
    this.consolEntity.GROUP_NAME_NOT_VALID = false;
    this.consolEntity.RETURN_DATE_NOT_VALID = false;
    this.consolEntity.FILER_TAX_YEAR_BEGIN_NOT_VALID = false;
    this.consolEntity.FILER_TAX_YEAR_END_NOT_VALID = false;
    this.consolEntity.SHORT_DISPLAY_NAME_NOT_VALID = false;
    this.consolEntity.GILTI_CONSOL_GROUP_KEY_NOT_VALID = false;

    let isValid = true;

    if (!this.consolEntity.CONSOL_GROUP_KEY) {
      this.consolEntity.CONSOL_GROUP_NOT_VALID = true;
      isValid = false;
    }
    if (!this.consolEntity.TAX_YEAR) {
      this.consolEntity.TAX_YEAR_NOT_VALID = true;
      isValid = false;
    }
    if (!this.consolEntity.GROUP_NAME) {
      this.consolEntity.GROUP_NAME_NOT_VALID = true;
      isValid = false;
    }
    if (!this.consolEntity.RETURN_DATE) {
      this.consolEntity.RETURN_DATE_NOT_VALID = true;
      isValid = false;
    }
    if (!this.consolEntity.FILER_TAX_YEAR_BEGIN) {
      this.consolEntity.FILER_TAX_YEAR_BEGIN_NOT_VALID = true;
      isValid = false;
    }
    if (!this.consolEntity.FILER_TAX_YEAR_END) {
      this.consolEntity.FILER_TAX_YEAR_END_NOT_VALID = true;
      isValid = false;
    }
    if (!this.consolEntity.SHORT_DISPLAY_NAME) {
      this.consolEntity.SHORT_DISPLAY_NAME_NOT_VALID = true;
      isValid = false;
    }
    if (!this.consolEntity.GILTI_CONSOL_GROUP_KEY) {
      this.consolEntity.GILTI_CONSOL_GROUP_KEY_NOT_VALID = true;
      isValid = false;
    }

    return isValid;
  }

  save() {
    if (!this.isValid()) {
      this._snackBar.open('Please fill all the required fields', 'Close', { duration: 4000, panelClass: ['warning-snackbar'] });
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;

    // Prepare data for saving - remove validation flags
    const saveData = { ...this.consolEntity };
    
    // Remove validation flag properties before API call
    delete saveData.CONSOL_GROUP_NOT_VALID;
    delete saveData.TAX_YEAR_NOT_VALID;
    delete saveData.GROUP_NAME_NOT_VALID;
    delete saveData.RETURN_DATE_NOT_VALID;
    delete saveData.FILER_TAX_YEAR_BEGIN_NOT_VALID;
    delete saveData.FILER_TAX_YEAR_END_NOT_VALID;
    delete saveData.SHORT_DISPLAY_NAME_NOT_VALID;
    delete saveData.GILTI_CONSOL_GROUP_KEY_NOT_VALID;
    
    saveData.EDIT_FLAG = this.isEdit ? 'U' : 'I';

    // Format dates
    if (saveData.RETURN_DATE) {
      saveData.RETURN_DATE = moment(saveData.RETURN_DATE).format('MM/DD/YYYY');
    }
    if (saveData.FILER_TAX_YEAR_BEGIN) {
      saveData.FILER_TAX_YEAR_BEGIN = moment(saveData.FILER_TAX_YEAR_BEGIN).format('MM/DD/YYYY');
    }
    if (saveData.FILER_TAX_YEAR_END) {
      saveData.FILER_TAX_YEAR_END = moment(saveData.FILER_TAX_YEAR_END).format('MM/DD/YYYY');
    }

    this.consolGroupService.saveConsolGroupForm(
      this.GENERAL_CONFIG.base_url,
      saveData,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        
        if (response.callSuccess === "1" && response.errorMessage === "null") {
          this._snackBar.open('Consol Group Updated successfully', 'Close', { duration: 4000, panelClass: ['success-snackbar'] });
          this.requestGridUpdate.emit('refresh');
        } else {
          this._snackBar.open(response.errorMessage || 'Error occurred while saving', 'Close', { duration: 4000, panelClass: ['error-snackbar'] });
        }
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', { duration: 4000, panelClass: ['error-snackbar'] });
        console.error('Save error:', error);
      }
    );
  }

  reset() {
    this.consolEntity = { ...this.consolEntity_backup };
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  // Helper method to format dates for HTML date inputs (YYYY-MM-DD)
  private formatDateForInput(dateValue: any): string {
    if (!dateValue) return '';
    
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return '';
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (e) {
      return '';
    }
  }
}
